import { Link } from "react-router-dom";
import { PortraitFrameImg } from "./PortraitFrame";
import { MessageCircle, ArrowDown, ArrowRight } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useContent } from "../context/ContentContext";
import { MEDIA, waLink } from "../lib/site";

const HeroBackdrop = () => (
  <div className="absolute inset-0 z-0 bg-ink">
    <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 78% 22%, rgba(212,175,55,0.12), rgba(200,98,46,0.04) 45%, transparent 62%)" }} />
    <div className="absolute right-0 inset-y-0 w-1/2 hidden lg:block">
      <img src={MEDIA.madeira} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-[0.08]" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
    </div>
  </div>
);

export const Hero = () => {
  const { lang, t, path } = useLang();
  const { content } = useContent();
  const hero = content?.hero?.[lang];

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-[92vh] flex items-center pt-24 pb-14 overflow-hidden">
      <HeroBackdrop />

      <div className="container-lux relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="overline mb-6">{hero?.overline}</div>

          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl font-light leading-[1.08] tracking-tight text-cream">
            {(hero?.titleLines || []).map((line, i) => (
              <span key={`${i}-${line}`} className="block pb-[0.04em]">{line}</span>
            ))}
          </h1>

          <div className="h-px w-40 bg-gold/50 grow-x my-8" style={{ animationDelay: "0.2s" }} />

          <p className="text-sand text-base md:text-lg leading-relaxed max-w-xl font-light">{hero?.subtitle}</p>

          <div className="flex flex-wrap gap-4 mt-10">
            <a href={waLink(lang)} target="_blank" rel="noreferrer" data-testid="hero-whatsapp" className="btn-solid btn-caps group">
              <MessageCircle size={15} strokeWidth={1.5} /> {t.common.whatsapp}
            </a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }} data-testid="hero-book" className="btn-gold btn-caps">
              {t.common.book}
            </a>
          </div>
        </div>

        <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
          <PortraitFrameImg withCaption />
          <Link to={path("historia")} data-testid="hero-story-link" className="group mt-8 flex items-center justify-center gap-4">
            <span className="h-px w-10 bg-gold/60 transition-[width] duration-500 group-hover:w-16" />
            <span className="text-[13px] md:text-sm tracking-[0.2em] uppercase font-sans font-semibold text-gold group-hover:text-gold-light transition-colors duration-300">{t.heroStory}</span>
            <ArrowRight size={18} strokeWidth={1.5} className="text-gold transition-transform duration-500 group-hover:translate-x-1.5" />
          </Link>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-gold/60">
        <span className="label-caps text-[9px] tracking-[0.3em]">{lang === "pt" ? "Descer" : "Scroll"}</span>
        <ArrowDown size={16} strokeWidth={1.5} className="animate-bounce" />
      </div>
    </section>
  );
};
